/* @flow */
import createDebugger from 'debug';
import { combineEpics, ofType } from 'redux-observable';
import { Observable } from 'rxjs';
import { debounceTime, mapTo } from 'rxjs/operators';
import { prop } from 'ramda';
import { stringify } from 'querystring';
import { REHYDRATE } from 'redux-persist-immutable/constants';
import * as RRR from 'react-router-redux';

import { validatePassword } from 'trustar-2.0/util';
import { LIGHT_THEME, NO_THEME } from 'trustar-2.0/themes/common/constants';

const debug = createDebugger('app:modules:app:epics'); // eslint-disable-line no-unused-vars
const errorDebugger = createDebugger('app:errors');

import * as getters from './getters.js';
import {
  SERVER_SESSION_TIME,
  KEEPALIVE_ENDPOINT,
  LOGIN_ENDPOINT,
  LOGIN_ENDPOINT_AUTHPATH,
  LOGOUT_ENDPOINT,
  SIGN_EULA_ENDPOINT,
  IS_LOGGED_IN_LOCALSTORAGE_KEY,
  STORED_USERNAME_LOCALSTORAGE_KEY,
  CHANGE_PASSWORD_ENDPOINT,
  COMING_FROM_APP,
  MFA_VERIFY_RESPONSE_ENDPOINT,
  FORGOT_PASSWORD_ENDPOINT,
  LOGIN_TIMEOUT_MS,
  ACTIVITY_LOCAL_STORAGE_POLL_FREQUENCY_MS,
  LAST_USER_ACTION_LOCALSTORAGE_KEY,
} from './constants.js';
import {
  FETCH_SERVER_SESSION_TIMEOUT_SUCCESS,
  SET_TIMEOUT_WARNING,
  INITIALIZE,
  INITIALIZE_SUCCESS,
  WARNING,
  LOGIN,
  LOGIN_SUCCESS,
  LOGIN_FAILURE,
  LOGIN_REDIRECT,
  LOGIN_REDIRECT_TO_EXTERNAL_APP,
  LOGOUT,
  LOGOUT_SUCCESS,
  LOGOUT_FAILURE,
  MFA_VERIFY_RESPONSE,
  MFA_VERIFY_RESPONSE_SUCCESS,
  MFA_VERIFY_RESPONSE_FAILURE,
  ACCEPT_TERMS,
  ACCEPT_TERMS_SUCCESS,
  ACCEPT_TERMS_FAILURE,
  CHANGE_PASSWORD_CREDENTIALS_UPDATE,
  CHANGE_PASSWORD_VALIDATE,
  CHANGE_PASSWORD,
  CHANGE_PASSWORD_SUCCESS,
  CHANGE_PASSWORD_FAILURE,
  FORGOT_PASSWORD,
  FORGOT_PASSWORD_SUCCESS,
  FORGOT_PASSWORD_FAILURE,
  CHANGE_PASSWORD_FAILURE_EXPIRED_TOKEN,
  CHANGE_PASSWORD_FAILURE_BAD_LINK,
  DECLINE_TERMS,
  CONFIRM_ALIVE,
  DID_LOGIN_VIA_SSO,
  LOGIN_CLEAR_STATUS_MESSAGE,
  LOGIN_CLEAR_STATUS_MESSAGE_SUCCESS,
  SET_THEME_FROM_PREFERENCES,
  SET_OPTIONS,
} from './action-types.js';

import Cookies from 'js-cookie';

import { DistinctSubscriber } from 'rxjs/operator/distinct';

const handleInactivity = ({ type }): Observable<*> => {
  debug('handling inactivity');
  switch (type) {
  case WARNING:
    return Observable.of({ type: SET_TIMEOUT_WARNING, payload: true });
  case LOGOUT:
    return Observable.of({ type: LOGOUT });
  default:
    return Observable.empty();
  }
};

const getUserActivityStream = ({ warningTime, logoutTime }) => {
  // if any click action is detected, emit the observable and write the change
  // to local storage so that other tabs can react.
  const clickSource = Observable.fromEvent(document.body, 'click')
    .debounceTime(1000)
    .do(() => localStorage.setItem(LAST_USER_ACTION_LOCALSTORAGE_KEY, (+(new Date())).toString()))
    .startWith(null); // Required to allow for the switchMap debouncing

  // Polls localstorage on a constant basis to see if any tab has seen action within
  // the current logout time window.
  const localStoragePoller = Observable.interval(ACTIVITY_LOCAL_STORAGE_POLL_FREQUENCY_MS)
    .map(() => +localStorage.getItem(LAST_USER_ACTION_LOCALSTORAGE_KEY))
    .distinctUntilChanged()
    .filter(x => +(new Date()) - x < logoutTime);

    // if either a new click happens or another tab has updated localStorage
  return Observable.merge(localStoragePoller,clickSource)
    .switchMap(() => Observable.merge(
      Observable.of({ type: WARNING }).delay(warningTime),
      Observable.of({ type: LOGOUT }).delay(logoutTime),
    ))
    .mergeMap(handleInactivity);
};

const keepAliveEpic = (action$, store, { ajax }) =>
  action$.ofType(INITIALIZE).mergeMap(() =>
    Observable.interval(SERVER_SESSION_TIME)
      .startWith(-1) // NOTE: -1 is arbitrary. Flow just wants a number not null
      .mergeMap(() => Observable.of({
        type: CONFIRM_ALIVE
      })));

/*
 * Note: the logic within confirmAliveEpic was originally in
 * keepAliveEpic, but it was hard to test and reason about.
 *
 * Upon error to /tools/session, it would log output to Sentry 
 * and then retry every minute until there was a success.
 * This retry logic caused infinite loops when the session
 * was invalidated through other means and caused issues
 * with the login page.
 *
 * I have removed this functionality.  There is a possibility
 * that /tools/session could be temporarily unavaible due to
 * brief server outage, but I think the platform is more stable
 * now then it was when this code was originally written.
 * We could put in a direct check on whether the error is type
 * 401 or other, but seriously...if the session check fails,
 * then the session should just end.
 *
 */
const confirmAliveEpic = (action$, store, {ajax}) =>
   action$.ofType(CONFIRM_ALIVE)
     .switchMap(() =>
        ajax.get(KEEPALIVE_ENDPOINT)
        .mergeMap(() => Observable.empty())
        .catch((err, obs) => {
            debug('Keep alive endpoint failed and logout command executed', err);
            return Observable.of({type: LOGOUT});
          }
        )
     );

const sessionTimeoutEpic = (action$, store, { ajax }) =>
  action$.ofType(INITIALIZE)
    .mergeMap(() =>
      ajax.get('/services/session-timeout')
        .map(prop('response'))
        .map(payload => ({
          type: FETCH_SERVER_SESSION_TIMEOUT_SUCCESS,
          payload,
        }))
        .catch(err =>
          Observable.of(err)
            .do(err => debug('Could not fetch server session timeout', err))
            .ignoreElements()));

const userActivityEpic = (action$, store) =>
  action$.ofType(INITIALIZE, FETCH_SERVER_SESSION_TIMEOUT_SUCCESS)
    .switchMap(() => {
      const state = store.getState();
      return getUserActivityStream({
        warningTime: getters.warningTime(state),
        logoutTime: getters.logoutTime(state),
      });
    })
    .do(x => debug('JUST SAW user activity', x));

/* TODO: after refactoring the app intialization actions in App.js
 * into epics this epic could listen for success for all of them: */
const appRehydration = (action$) =>
  action$.ofType(REHYDRATE)
    .mapTo({ type: INITIALIZE_SUCCESS });


const failedActionRe = /(.+)_FAILURE$/;
const isFailedAction = x => failedActionRe.test(x);

const alertsEpic = action$ =>
  action$.filter(action => action.error || isFailedAction(action))
    .do(action => errorDebugger('exception:', action.type, action.payload))
    .ignoreElements();

const clearLoginStatusMessageEpic = (action$) =>
  action$.ofType(LOGIN_CLEAR_STATUS_MESSAGE)
    .delay(3000)
    .debounceTime(500)
    .map(() => ({ type: LOGIN_CLEAR_STATUS_MESSAGE_SUCCESS }));

const loginEpic = (action$, store, {ajax, localStorage}) => {
  return action$.ofType(LOGIN)
    .switchMap(action =>
      ajax.post(LOGIN_ENDPOINT, stringify(action.payload), {}, {timeout: LOGIN_TIMEOUT_MS})
        .do(() => {
          localStorage.setItem(STORED_USERNAME_LOCALSTORAGE_KEY, action.payload.username);
          return action.payload.cfa ?
            localStorage.setItem(COMING_FROM_APP, action.payload.cfa) :
            localStorage.removeItem(COMING_FROM_APP);
        })
        .mergeMap(({response}) => {
          if (action.payload.cfa) { // cfa - Coming From App
            return Observable.of({
              type: LOGIN_REDIRECT_TO_EXTERNAL_APP, // Upon success will take you back to the app from which you came
              payload: {rta: action.payload.cfa, ...response} // rta - Return To App
            });
          } else {
            return Observable.of({
              type: LOGIN_REDIRECT, // Login Redirect refers to an internal route
              payload: response
            });
          }
        })
        .catch(err => (Observable.of({
          type: LOGIN_FAILURE,
          payload: err,
          error: true,
        })))
    );
};

const eulaRedirectEpic = (action$, store, { ajax }) => {
  return action$.ofType(LOGIN_REDIRECT)
    .pluck('payload', 'state')
    .filter((state) => state === 'REQUIRES_EULA')
    .do((action) => store.dispatch(RRR.replace('/auth/eula')))
    .ignoreElements();
};

const mfaRedirectEpic = (action$, store, { ajax }) => {
  return action$.ofType(LOGIN_REDIRECT)
    .pluck('payload', 'state')
    .filter((state) => state === 'REQUIRES_MFA')
    .do((action) => store.dispatch(RRR.replace('/auth/mfa')))
    .ignoreElements();
};

const authenticatedRedirectEpic = (action$, store, { ajax, localStorage }) => {
  return action$.ofType(LOGIN_REDIRECT)
    .pluck('payload', 'state')
    .filter((state) => state === 'AUTHENTICATED')
    .do((action) => localStorage.setItem(IS_LOGGED_IN_LOCALSTORAGE_KEY, true))
    .do((action) => {
      const intendedPath = getters.intendedPath(store.getState());
      if(intendedPath.match(/^\/(auth|$)/)) {
        store.dispatch(RRR.replace('/'));
      }
      else {
        store.dispatch(RRR.replace(intendedPath));
      }
    })
    .map(() => ({
      type: LOGIN_SUCCESS,
    }));
};

// Subject to change, currently want to a totally separate epic for any external micro FE app uses
const authenticatedRedirectToExternalAppEpic = (action$) => {
  return action$.ofType(LOGIN_REDIRECT_TO_EXTERNAL_APP)
    .pluck('payload')
    .filter((payload) => payload.state === 'AUTHENTICATED')
    .do((payload) => {
      // TODO Formalize this flow
      const returnToAppUrl = atob(payload.rta);
      location.href = returnToAppUrl.toString();
    })
    .map(() => ({
      type: LOGIN_SUCCESS,
    }));
};
const logoutEpic = (action$, store, {ajax, localStorage}) => {
  return action$.ofType(LOGOUT)
    .do((action) => {
      return action.payload && action.payload.cfa ?
        localStorage.setItem(COMING_FROM_APP, action.payload.cfa) :
        localStorage.removeItem(COMING_FROM_APP);
    })
    .mergeMap(action => ajax.post(LOGOUT_ENDPOINT, action.payload, {}, {timeout: 5000}))
    .mergeMap(({response}) => Observable.of({
      type: LOGOUT_SUCCESS,
      payload: response,
    }))
    .catch(err => Observable.of({
      type: LOGOUT_FAILURE,
      payload: err,
      error: true,
    }))
    .finally()
    // Even if the logout api request fails, we should still
    // Invalidate the session in the browser and redirect to login.
    .do(() => {
      const comingFromApp = localStorage.getItem(COMING_FROM_APP);

      localStorage.removeItem(STORED_USERNAME_LOCALSTORAGE_KEY);
      localStorage.removeItem(IS_LOGGED_IN_LOCALSTORAGE_KEY);
      localStorage.removeItem(COMING_FROM_APP);

      Cookies.remove('XSRF-TOKEN', { domain: '.station.trustar.co' });
      Cookies.remove('XSRF-TOKEN', { domain: 'station.trustar.co' });
      Cookies.remove('XSRF-TOKEN');

      if (window.location.pathname !== LOGIN_ENDPOINT_AUTHPATH) {
        // If the user clicked logout from one of our microservices, after logout append their app address so they can return there. TBD If this is good UX
        const loginWithPossibleAppLink = comingFromApp ?
          `${LOGIN_ENDPOINT_AUTHPATH}?${stringify({cfa: comingFromApp})}` : LOGIN_ENDPOINT_AUTHPATH;

        window.location.replace(loginWithPossibleAppLink);
      }
    });
};

const acceptTermsEpic = (action$, store, { ajax}) =>{
  return action$.ofType(ACCEPT_TERMS)
    .mergeMap((action) => ajax.post(SIGN_EULA_ENDPOINT),
    (action, response) => ({
      type: LOGIN_REDIRECT,
      payload: {
        state: 'AUTHENTICATED'
      }
    }))
    .catch(err => Observable.of({
      type: ACCEPT_TERMS_FAILURE,
      payload: err,
      error: true,
    }));
};

const createPasswordUpdateCredentialsEpic = (action$, store, {ajax}) => {
  return action$.ofType(CHANGE_PASSWORD_CREDENTIALS_UPDATE)
    .debounceTime(200)
    .map((action) => {
      const isValid = validatePassword(action.payload);
      return {
        type: CHANGE_PASSWORD_VALIDATE,
        payload: isValid,
      };
    });
};

const changePasswordEpic = (action$, store, { ajax }) =>{
  return action$.ofType(CHANGE_PASSWORD)
  .mergeMap(action => ajax.postJSON(CHANGE_PASSWORD_ENDPOINT, action.payload))
  .map(({ response }) => ({
    type: CHANGE_PASSWORD_SUCCESS,
    payload: response,
  }))
  .do(() => store.dispatch(RRR.replace('/auth/logout')))
  .catch(err => {
    let type = CHANGE_PASSWORD_FAILURE;
    if (err.status === 404) {
      type = CHANGE_PASSWORD_FAILURE_BAD_LINK;
    }
    else if (err.status === 400) {
      type = CHANGE_PASSWORD_FAILURE_EXPIRED_TOKEN;
    }
    return Observable.of({
      type: type,
      payload: err,
      error: true,
    })
    .do(() => store.dispatch(RRR.replace('/auth/login')));
  })
};

const mfaVerifyResponseEpic = (action$, store, {ajax}) => {
  return action$.ofType(MFA_VERIFY_RESPONSE)
    .mergeMap((action) => ajax.postJSON(MFA_VERIFY_RESPONSE_ENDPOINT, action.payload))
    .map(({ response }) => ({
      type: LOGIN_REDIRECT,
      payload: {
        state: 'AUTHENTICATED'
      }
    }))
    .catch(err => Observable.of({
      type: MFA_VERIFY_RESPONSE_FAILURE,
      payload: err,
      error: true,
    }));
};

const mfaVerifyResponseFailureEpic = (action$, store, {ajax}) => {
  return action$.ofType(MFA_VERIFY_RESPONSE_FAILURE)
    .do(() => store.dispatch(RRR.replace(LOGIN_ENDPOINT_AUTHPATH)))
    .ignoreElements();
};

const forgotPasswordEpic = (action$, store, { ajax }) =>{
  return action$.ofType(FORGOT_PASSWORD)
  .mergeMap(action => ajax.postJSON(FORGOT_PASSWORD_ENDPOINT, action.payload))
  .map(({ response }) => ({
    type: FORGOT_PASSWORD_SUCCESS,
    payload: response,
  }))
  .catch(err => Observable.of({
    type: FORGOT_PASSWORD_FAILURE,
    payload: err,
    error: true,
  }))
  .finally()
    .do(() => store.dispatch(RRR.replace('/auth/login')));
};

const declineTermsEpic = (action$, store, { ajax }) =>{
  return action$.ofType(DECLINE_TERMS)
    .do(() => store.dispatch(RRR.replace(LOGIN_ENDPOINT_AUTHPATH)))
    .ignoreElements();
};

// If User goes directly to /auth/sso, go ahead and act like they are logged in.
// The /tools/session call will log them out if they haven't indeed come from SSO.
const didLoginViaSSOEpic = (action$, store, {ajax, localStorage}) => {
  return action$.ofType(DID_LOGIN_VIA_SSO)
    .do(() => localStorage.setItem(STORED_USERNAME_LOCALSTORAGE_KEY, ''))
    .do(() => localStorage.setItem(IS_LOGGED_IN_LOCALSTORAGE_KEY, true))
    .do((action) => store.dispatch(RRR.replace(action.payload)))
    .ignoreElements();
};

const setThemeFromPreferences = (action$) => {
  return action$.ofType(SET_THEME_FROM_PREFERENCES)
    .pluck('payload')
    .map((themeMode) => {
      const theme = themeMode === NO_THEME ? LIGHT_THEME : themeMode.toLowerCase();

      return {
        type: SET_OPTIONS,
        payload: { theme },
      };
    });
};

export default combineEpics(
  alertsEpic,
  clearLoginStatusMessageEpic,
  confirmAliveEpic,
  userActivityEpic,
  sessionTimeoutEpic,
  keepAliveEpic,
  appRehydration,
  loginEpic,
  mfaRedirectEpic,
  eulaRedirectEpic,
  authenticatedRedirectEpic,
  logoutEpic,
  acceptTermsEpic,
  createPasswordUpdateCredentialsEpic,
  changePasswordEpic,
  mfaVerifyResponseEpic,
  mfaVerifyResponseFailureEpic,
  forgotPasswordEpic,
  declineTermsEpic,
  didLoginViaSSOEpic,
  authenticatedRedirectToExternalAppEpic,
  setThemeFromPreferences,
);



// WEBPACK FOOTER //
// ./src/app/modules/app/epics.js